import { useState, useEffect, useRef } from "react";

const gold = "#f5a623", blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e", red = "#ef4444", purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const ss = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>{text}</span>;
}

// ── S1: Regulatory Structure Hierarchy ──
function RegHierarchy() {
  const [active, setActive] = useState(null);
  const layers = [
    {
      level: "01", icon: "🏛️", name: "Provincial Governments", color: gold,
      role: "Ultimate Authority — create and enforce securities law",
      desc: "Securities regulation in Canada is a provincial responsibility. Each of the 13 provinces and territories has its own Securities Commission or Administrator. There is no single federal securities regulator.",
      examples: ["Ontario Securities Commission (OSC)", "BC Securities Commission (BCSC)", "Autorité des marchés financiers (AMF — Quebec)", "Alberta Securities Commission (ASC)"],
      key: "13 separate regulators. Each province has jurisdiction over its own capital markets.",
    },
    {
      level: "02", icon: "🤝", name: "Canadian Securities Administrators (CSA)", color: blue2,
      role: "Umbrella Coordinator — harmonizes rules across all 13 regulators",
      desc: "The CSA is not a separate regulator — it's an umbrella body that coordinates the 13 provincial commissions. Its purpose is to make rules consistent across Canada so the industry doesn't face 13 different sets of rules.",
      examples: ["Issues National Instruments (NI) — adopted by all provinces", "Passport System — one principal regulator for multi-provincial firms", "NI 81-102, NI 81-101, NI 81-105"],
      key: "The Passport System allows a firm registered in one province to operate in others without re-registering everywhere.",
    },
    {
      level: "03", icon: "⚖️", name: "CIRO", color: purple,
      role: "SRO — regulates dealers and their representatives day-to-day",
      desc: "The Canadian Investment Regulatory Organization (CIRO) was formed January 1, 2023 by amalgamating the MFDA (Mutual Fund Dealers Association) and IIROC (Investment Industry Regulatory Organization of Canada) into a single unified SRO.",
      examples: ["Regulates investment dealers and mutual fund dealers", "Can fine, suspend, or expel members", "Sets proficiency standards (e.g., IFC exam requirement)", "Cannot imprison — only courts have that power"],
      key: "CIRO = MFDA + IIROC (merged Jan 1, 2023). An SRO derives authority from the provincial regulators who recognize it.",
    },
    {
      level: "04", icon: "🔍", name: "OBSI", color: green,
      role: "Independent Dispute Resolution — handles client complaints",
      desc: "The Ombudsman for Banking Services and Investments (OBSI) provides free, independent dispute resolution when a client is not satisfied with how their dealer or bank handled a complaint.",
      examples: ["Free service for clients", "Recommendations are non-binding but rarely ignored", "Last resort after exhausting dealer's internal process", "Covers banking and investment complaints"],
      key: "OBSI is the escalation path after the dealer's internal complaint process. Non-binding but morally authoritative.",
    },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      {layers.map((layer, i) => (
        <div key={i} style={{ marginBottom: 8 }}>
          <div onClick={() => setActive(active === i ? null : i)}
            style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? layer.color + "55" : border}`, borderRadius: 8, padding: "16px 20px", cursor: "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 42, height: 42, borderRadius: 8, background: `${layer.color}18`, border: `1px solid ${layer.color}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem", flexShrink: 0 }}>{layer.icon}</div>
              <div>
                <div style={{ fontFamily: "monospace", fontSize: "0.62rem", color: layer.color, letterSpacing: "0.12em", textTransform: "uppercase" }}>Level {layer.level}</div>
                <div style={{ fontSize: "0.92rem", fontWeight: 700, color: white }}>{layer.name}</div>
                <div style={{ fontSize: "0.74rem", color: muted, marginTop: 2 }}>{layer.role}</div>
              </div>
            </div>
            <span style={{ color: active === i ? layer.color : muted, fontSize: "1rem", transform: active === i ? "rotate(90deg)" : "none", transition: "transform 0.25s", flexShrink: 0 }}>›</span>
          </div>
          {active === i && (
            <div style={{ background: navy4, border: `1px solid ${layer.color}22`, borderRadius: "0 0 8px 8px", padding: 22, marginTop: -1 }}>
              <div style={{ fontSize: "0.85rem", color: textCol, lineHeight: 1.75, marginBottom: 14 }}>{layer.desc}</div>
              <div style={{ display: "grid", gap: 5, marginBottom: 14 }}>
                {layer.examples.map((ex, j) => (
                  <div key={j} style={{ fontSize: "0.82rem", color: textCol, display: "flex", gap: 8 }}>
                    <span style={{ color: layer.color, flexShrink: 0 }}>→</span>{ex}
                  </div>
                ))}
              </div>
              <div style={{ background: navy3, borderLeft: `3px solid ${layer.color}`, borderRadius: 6, padding: "10px 14px", fontSize: "0.8rem", color: muted, lineHeight: 1.65 }}>
                💡 {layer.key}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── S2: National Instruments ──
function NITable() {
  const [active, setActive] = useState(null);
  const nis = [
    {
      code: "NI 81-101", focus: "Disclosure", color: blue2,
      headline: "Mandates the Fund Facts document and Simplified Prospectus.",
      details: "Requires every mutual fund to produce a Fund Facts document — a plain-language, 2-page summary of the fund's key information: costs, risk rating, past performance, and top holdings. Must be delivered to investors before or at the time of purchase.",
      keyPoints: ["Fund Facts must be delivered before purchase", "Simplified Prospectus for more detailed disclosure", "Plain-language requirement — no jargon", "Standardized format across all funds"],
    },
    {
      code: "NI 81-102", focus: "Fund Operations", color: gold,
      headline: "Governs what mutual funds can and cannot do — investment restrictions.",
      details: "The main rulebook for how mutual funds operate. Sets limits on what a fund can invest in, how much leverage it can use, how derivatives can be used, and short-selling rules. Alternative Mutual Funds (Liquid Alts) have more relaxed rules under this instrument.",
      keyPoints: ["Maximum 10% of fund in one issuer (diversification)", "Restrictions on derivatives use", "Leverage limits for conventional funds", "Alternative mutual funds get more flexibility"],
    },
    {
      code: "NI 81-105", focus: "Sales Practices", color: purple,
      headline: "Regulates compensation and marketing. Bans conflicts of interest.",
      details: "Governs how fund companies can interact with dealers and advisors. Prohibits practices that could compromise the advisor's objectivity — like paying for vacations disguised as conferences, excessive gifts, or tied selling.",
      keyPoints: ["Bans excessive gifts and entertainment", "Prohibits paying for advisor vacations ('conferences')", "Allows co-op marketing within strict dollar limits", "Bans tied selling (e.g., 'buy this fund or no mortgage')"],
    },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gap: 8 }}>
        {nis.map((ni, i) => (
          <div key={i} onClick={() => setActive(active === i ? null : i)}
            style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? ni.color + "55" : border}`, borderRadius: 8, padding: "16px 20px", cursor: "pointer", transition: "all 0.2s" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{ fontFamily: "monospace", fontSize: "0.88rem", fontWeight: 700, color: ni.color, background: `${ni.color}14`, border: `1px solid ${ni.color}33`, padding: "4px 10px", borderRadius: 6, whiteSpace: "nowrap" }}>{ni.code}</span>
                <div>
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: ni.color }}>{ni.focus}</div>
                  <div style={{ fontSize: "0.84rem", color: textCol, marginTop: 2 }}>{ni.headline}</div>
                </div>
              </div>
              <span style={{ color: active === i ? ni.color : muted, fontSize: "1rem", transform: active === i ? "rotate(90deg)" : "none", transition: "transform 0.25s", flexShrink: 0 }}>›</span>
            </div>
            {active === i && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${border}` }}>
                <div style={{ fontSize: "0.84rem", color: textCol, lineHeight: 1.75, marginBottom: 12 }}>{ni.details}</div>
                {ni.keyPoints.map((kp, j) => (
                  <div key={j} style={{ fontSize: "0.81rem", color: textCol, display: "flex", gap: 8, marginBottom: 4 }}>
                    <span style={{ color: ni.color, flexShrink: 0 }}>→</span>{kp}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── S3: Registration ──
function RegistrationPanel() {
  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10, marginBottom: 16 }}>
        {[
          { icon: "📝", title: "NRD — National Registration Database", color: blue2, body: "A web-based system where dealers and advisors file registration applications electronically. All registration is managed through NRD." },
          { icon: "📄", title: "Form 33-109F4", color: gold, body: "The standard form an individual submits when applying for registration as a Dealing Representative. Filed through NRD." },
          { icon: "👤", title: "Dealing Representative", color: purple, body: "The registration category for mutual fund salespeople. Requirements: Pass IFC (or equivalent), be sponsored by a registered dealer, and be of 'good character.'" },
          { icon: "🏢", title: "Dealer Sponsorship", color: green, body: "You cannot be registered independently. A Dealing Representative must be employed by and sponsored by a registered dealer." },
        ].map((card, i) => (
          <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 16px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: card.color }} />
            <div style={{ fontSize: "1.2rem", marginBottom: 8 }}>{card.icon}</div>
            <div style={{ fontSize: "0.82rem", fontWeight: 700, color: card.color, marginBottom: 5 }}>{card.title}</div>
            <div style={{ fontSize: "0.79rem", color: muted, lineHeight: 1.65 }}>{card.body}</div>
          </div>
        ))}
      </div>
      <div style={{ background: navy3, border: `1px solid ${gold}33`, borderRadius: 8, padding: "18px 20px" }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.9rem", color: white, marginBottom: 10 }}>📋 Registration Requirements — Dealing Representative</div>
        <div style={{ display: "grid", gap: 6 }}>
          {[["Proficiency", "Pass the IFC exam (Investment Funds in Canada) or an approved equivalent", blue2], ["Employment", "Must be employed by a CIRO-registered mutual fund dealer (no self-sponsorship)", gold], ["Character", "Must be of good character — no serious criminal history, no regulatory bars", green], ["Ongoing", "Continuing education (CE) requirements must be maintained annually", purple]].map(([lbl, val, col]) => (
            <div key={lbl} style={{ background: navy4, borderRadius: 6, padding: "9px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, width: 90, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
              <div style={{ fontSize: "0.82rem", color: textCol, flex: 1, lineHeight: 1.6 }}>{val}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── S4: CFR & Compliance ──
function CFRPanel() {
  const [active, setActive] = useState(0);
  const pillars = [
    {
      icon: "📋", name: "RDI", full: "Relationship Disclosure Information", color: blue2,
      when: "At account opening — before any transactions",
      what: "A written document explaining the client–advisor relationship. Must cover: the types of products sold, all applicable fees and charges, the risks involved, and any conflicts of interest.",
      why: "Ensures clients understand exactly what they are getting into before committing money. Mandated by the CFRs (implemented December 2021).",
      mustInclude: ["Products and services offered by the dealer", "All fees, charges, and costs", "Types of risk clients may face", "Conflicts of interest that exist"],
    },
    {
      icon: "⚠️", name: "Conflicts", full: "Conflict of Interest Management", color: red,
      when: "Whenever a material conflict exists or arises",
      what: "A Material Conflict is any conflict that could reasonably be expected to affect the advisor's recommendations or the client's interest. The CFR framework requires: DISCLOSE and, if it cannot be managed in the client's best interest, AVOID.",
      why: "Puts client interest first. Advisors can no longer simply pocket higher commissions on proprietary products without full disclosure and a suitability analysis.",
      mustInclude: ["Proprietary fund recommendation → higher commission", "Referral arrangements → fee paid for sending clients", "Owning shares in a company you recommend", "Family relationships with fund companies"],
    },
    {
      icon: "🔎", name: "KYP", full: "Know Your Product", color: gold,
      when: "Before recommending any product",
      what: "Advisors must understand the structure, features, risks, costs, and liquidity of every product they recommend. Recommending a product you don't understand is a compliance violation — not just bad practice.",
      why: "Ensures suitability analysis is meaningful. You cannot match a product to a client's needs if you don't understand the product.",
      mustInclude: ["Product structure and how it works", "All costs (MER, TER, loads)", "Liquidity — when can the client sell?", "Risks — what could go wrong?"],
    },
    {
      icon: "👤", name: "KYC", full: "Know Your Client", color: green,
      when: "At account opening and reviewed regularly",
      what: "Collecting and maintaining accurate information about the client: identity, financial situation, investment knowledge, risk tolerance, risk capacity, time horizon, and investment objectives.",
      why: "The foundation of suitability. Without KYC, the advisor cannot determine if a product is appropriate. Must be updated when circumstances change.",
      mustInclude: ["Identity verification (government photo ID)", "Financial situation (income, assets, liabilities)", "Risk tolerance and capacity", "Investment objectives and time horizon"],
    },
  ];
  const p = pillars[active];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 0 }}>
        {pillars.map((pl, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ flex: 1, minWidth: 70, padding: "11px 8px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? pl.color + "55" : border}`, borderBottom: active === i ? `2px solid ${pl.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: active === i ? pl.color : muted, transition: "all 0.2s", textAlign: "center" }}>
            <div style={{ fontSize: "1rem", marginBottom: 3 }}>{pl.icon}</div>
            {pl.name}
          </button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${p.color}22`, borderRadius: "0 0 8px 8px", padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 4 }}>{p.icon} {p.full}</div>
        <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
          {[["When", p.when, blue2], ["What It Is", p.what, textCol], ["Why It Matters", p.why, green]].map(([lbl, val, col]) => (
            <div key={lbl} style={{ background: navy3, borderRadius: 6, padding: "10px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: p.color, width: 90, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
              <div style={{ fontSize: "0.83rem", color: col, flex: 1, lineHeight: 1.65 }}>{val}</div>
            </div>
          ))}
        </div>
        <div style={{ background: navy3, borderLeft: `3px solid ${p.color}`, borderRadius: 6, padding: "11px 16px" }}>
          <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: p.color, marginBottom: 8 }}>Must Include / Examples</div>
          {p.mustInclude.map((item, j) => (
            <div key={j} style={{ fontSize: "0.81rem", color: textCol, display: "flex", gap: 8, marginBottom: 4 }}>
              <span style={{ color: p.color, flexShrink: 0 }}>→</span>{item}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── S5: FINTRAC ──
function FINTRACPanel() {
  const [cashA, setCashA] = useState(6000);
  const [cashB, setCashB] = useState(5000);
  const total = parseFloat(cashA) + parseFloat(cashB);
  const mustReport = total >= 10000;

  const reports = [
    {
      icon: "💵", name: "Large Cash Transaction Report", color: gold, threshold: "$10,000+",
      desc: "Any cash transaction of $10,000 or more within a single 24-hour period — including multiple transactions that total $10,000+. Must be filed within 15 days.",
      trigger: "Client deposits $6,000 at 10 AM and $5,000 at 2 PM. Total = $11,000 → Must report.",
    },
    {
      icon: "🔍", name: "Suspicious Transaction Report (STR)", color: red, threshold: "No threshold",
      desc: "Filed whenever there are reasonable grounds to suspect a transaction is related to money laundering or terrorist financing — regardless of the dollar amount. No minimum required.",
      trigger: "Client tries to make an unusual series of small cash deposits just under $10,000 each (structuring). Suspicious → Must report.",
    },
    {
      icon: "🚨", name: "Terrorist Property Report", color: purple, threshold: "No threshold",
      desc: "Filed if you know or have reason to believe that any property is owned or controlled by a terrorist group or for terrorist purposes. Immediate filing required.",
      trigger: "Name on a government sanctions list matches a client account. Must report immediately.",
    },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gap: 10, marginBottom: 20 }}>
        {reports.map((r, i) => (
          <div key={i} style={{ background: navy3, border: `1px solid ${r.color}33`, borderRadius: 8, padding: "18px 20px" }}>
            <div style={{ display: "flex", align: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: "1.2rem" }}>{r.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.88rem", fontWeight: 700, color: white }}>{r.name}</div>
                <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 10px", borderRadius: 100, color: r.color, background: `${r.color}18`, border: `1px solid ${r.color}33` }}>Threshold: {r.threshold}</span>
              </div>
            </div>
            <div style={{ fontSize: "0.83rem", color: textCol, lineHeight: 1.7, marginBottom: 8 }}>{r.desc}</div>
            <div style={{ background: navy4, borderLeft: `3px solid ${r.color}`, borderRadius: 5, padding: "8px 12px", fontSize: "0.77rem", color: muted, lineHeight: 1.65 }}>
              📌 Example: {r.trigger}
            </div>
          </div>
        ))}
      </div>

      {/* FINTRAC 24h Calculator */}
      <div style={{ background: navy3, border: `1px solid ${gold}33`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>🧮 24-Hour Cash Transaction Checker</div>
        <p style={{ fontSize: "0.82rem", color: muted, marginBottom: 16, lineHeight: 1.65 }}>
          Multiple cash transactions within a 24-hour period are aggregated. If the total reaches $10,000, a Large Cash Transaction Report must be filed.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          {[["Transaction 1 ($)", cashA, setCashA], ["Transaction 2 ($)", cashB, setCashB]].map(([lbl, val, set], i) => (
            <div key={i}>
              <label style={{ display: "block", fontSize: "0.62rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>{lbl}</label>
              <input type="number" value={val} onChange={e => set(e.target.value)}
                style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.9rem", outline: "none" }} />
            </div>
          ))}
        </div>
        <div style={{ background: navy4, border: `1px solid ${mustReport ? red : green}44`, borderRadius: 8, padding: "14px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: muted, marginBottom: 2 }}>24-Hour Total</div>
              <div style={{ fontFamily: "monospace", fontSize: "1.6rem", fontWeight: 700, color: mustReport ? red : green }}>${total.toLocaleString()}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "0.85rem", fontWeight: 700, color: mustReport ? red : green, marginBottom: 4 }}>
                {mustReport ? "🚨 MUST FILE Large Cash Transaction Report" : "✅ No Reporting Required"}
              </div>
              <div style={{ fontSize: "0.78rem", color: muted, lineHeight: 1.6 }}>
                {mustReport ? `Total of $${total.toLocaleString()} ≥ $10,000 threshold. Report must be filed with FINTRAC within 15 days.` : `Total of $${total.toLocaleString()} is below the $10,000 threshold. No FINTRAC report required unless suspicious.`}
              </div>
            </div>
          </div>
          <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted, marginTop: 10, borderTop: `1px solid ${border}`, paddingTop: 8 }}>
            → $${parseFloat(cashA).toLocaleString()} + $${parseFloat(cashB).toLocaleString()} = $${total.toLocaleString()} {mustReport ? "≥ $10,000 → Report" : "< $10,000 → No report"}
          </div>
        </div>
      </div>

      {/* PEP callout */}
      <Callout icon="👔" accent={purple}>
        <strong style={{ color: purple }}>Politically Exposed Persons (PEPs):</strong> Advisors must determine if a client is a senior foreign official (head of state, senior government member, general, judge) or their close family member. PEPs are considered <strong>high-risk for corruption and money laundering</strong> — enhanced due diligence is required. Client identity must always be verified with <strong>government-issued photo ID</strong> when opening an account.
      </Callout>
    </div>
  );
}

// ── S6: Privacy & DNCL ──
function PrivacyPanel() {
  const [callType, setCallType] = useState("existing");
  const configs = {
    existing: {
      label: "Existing Client", icon: "🤝", color: green,
      canCall: true,
      rule: "You CAN call an existing client for up to 18 months after their last transaction, even if they are on the DNCL.",
      period: "18 months after last transaction",
    },
    inquiry: {
      label: "Prospect — Made Inquiry", icon: "📞", color: blue2,
      canCall: true,
      rule: "If a prospect contacts you or makes an inquiry (e.g., visits website, fills out a form), you may call them for 6 months — even if they are on the DNCL.",
      period: "6 months after inquiry",
    },
    referral: {
      label: "Referral", icon: "👋", color: gold,
      canCall: true,
      rule: "If someone is referred to you, you may make ONE call to that person — even if they are on the DNCL.",
      period: "One call only",
    },
    dncl: {
      label: "DNCL — No Relationship", icon: "🚫", color: red,
      canCall: false,
      rule: "If a number is on the DNCL and there is no existing relationship, inquiry, or referral — you CANNOT call. Violations result in significant fines.",
      period: "Cannot call",
    },
  };
  const cfg = configs[callType];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10, marginBottom: 20 }}>
        <div style={{ background: navy3, border: `1px solid ${blue2}33`, borderRadius: 8, padding: "18px 16px" }}>
          <div style={{ fontSize: "1.2rem", marginBottom: 8 }}>🔒</div>
          <div style={{ fontSize: "0.82rem", fontWeight: 700, color: blue2, marginBottom: 6 }}>PIPEDA</div>
          <div style={{ fontSize: "0.78rem", color: muted, lineHeight: 1.65 }}>Personal Information Protection and Electronic Documents Act. Governs how private sector organizations collect, use, and disclose personal information. Key rule: <strong style={{ color: textCol }}>consent required</strong> to collect data; data can only be used for the stated purpose.</div>
        </div>
        <div style={{ background: navy3, border: `1px solid ${gold}33`, borderRadius: 8, padding: "18px 16px" }}>
          <div style={{ fontSize: "1.2rem", marginBottom: 8 }}>📵</div>
          <div style={{ fontSize: "0.82rem", fontWeight: 700, color: gold, marginBottom: 6 }}>National Do Not Call List (DNCL)</div>
          <div style={{ fontSize: "0.78rem", color: muted, lineHeight: 1.65 }}>Operated by the CRTC. Telemarketers are prohibited from calling numbers registered on the DNCL. Three exceptions permit contact: existing clients, inquiries, and referrals.</div>
        </div>
      </div>

      {/* DNCL Exception Explorer */}
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 14 }}>📵 DNCL — Can You Call? Explorer</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 6, marginBottom: 18 }}>
          {Object.entries(configs).map(([key, val]) => (
            <button key={key} onClick={() => setCallType(key)} style={{ padding: "10px 8px", background: callType === key ? navy4 : navy3, border: `1px solid ${callType === key ? val.color + "55" : border}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: "0.72rem", fontWeight: 700, color: callType === key ? val.color : muted, transition: "all 0.2s", textAlign: "center" }}>
              <div style={{ fontSize: "1rem", marginBottom: 4 }}>{val.icon}</div>
              {val.label}
            </button>
          ))}
        </div>
        <div style={{ background: navy4, border: `1px solid ${cfg.color}44`, borderRadius: 8, padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <span style={{ fontSize: "1.4rem" }}>{cfg.icon}</span>
            <div>
              <div style={{ fontSize: "0.85rem", fontWeight: 700, color: cfg.canCall ? green : red }}>{cfg.canCall ? "✅ CAN Call" : "🚫 CANNOT Call"}</div>
              <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: cfg.color }}>{cfg.period}</div>
            </div>
          </div>
          <div style={{ fontSize: "0.84rem", color: textCol, lineHeight: 1.7 }}>{cfg.rule}</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 14 }}>
          {[["Existing Client", "18 months", green], ["Inquiry", "6 months", blue2], ["Referral", "1 call", gold]].map(([lbl, val, col]) => (
            <div key={lbl} style={{ background: navy4, border: `1px solid ${col}33`, borderRadius: 6, padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, marginBottom: 4 }}>{lbl}</div>
              <div style={{ fontFamily: "monospace", fontSize: "0.88rem", fontWeight: 700, color: white }}>{val}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Quiz ──
const questions = [
  { q: "Which organization was formed in 2023 to regulate investment and mutual fund dealers?", opts: ["The CSA", "The OSC", "CIRO", "The MFDA"], correct: 2, fb: { y: "Correct! CIRO — the Canadian Investment Regulatory Organization — was formed January 1, 2023 by merging the MFDA (Mutual Fund Dealers Association) and IIROC into a single unified SRO.", n: "Not quite. CIRO was formed on January 1, 2023 by amalgamating the MFDA and IIROC. The CSA is the umbrella of provincial regulators. The OSC is Ontario's provincial regulator." } },
  { q: "National Instrument 81-102 primarily governs:", opts: ["The registration of advisors", "Mutual fund operations and investment restrictions", "Sales practices and incentives", "Privacy laws"], correct: 1, fb: { y: "Correct! NI 81-102 is the main rulebook for how mutual funds operate — investment restrictions, leverage limits, derivatives rules, and short-selling constraints.", n: "Not quite. NI 81-102 = fund Operations. NI 81-101 = Disclosure (Fund Facts). NI 81-105 = Sales Practices. Registration is governed by separate provincial requirements." } },
  { q: "What is the minimum dollar threshold for filing a 'Large Cash Transaction Report' with FINTRAC?", opts: ["$5,000", "$10,000", "$50,000", "$100,000"], correct: 1, fb: { y: "Correct! Any cash transaction(s) totalling $10,000 or more within a single 24-hour period must be reported to FINTRAC as a Large Cash Transaction Report — even if broken into multiple smaller deposits.", n: "Not quite. The FINTRAC Large Cash Transaction threshold is $10,000 — not $5,000. Transactions are aggregated over a 24-hour period. $6,000 + $5,000 in one day = $11,000 → must report." } },
  { q: "If an advisor has a conflict of interest that cannot be resolved in the client's best interest, the advisor must:", opts: ["Disclose it and proceed anyway", "Ask the branch manager for permission", "Avoid the activity or transaction entirely", "Reduce their commission by 50%"], correct: 2, fb: { y: "Correct! Under the Client Focused Reforms: DISCLOSE conflicts and, if they cannot be managed in the client's best interest, you must AVOID the transaction. Disclosure alone is not enough if the conflict harms the client.", n: "Not quite. CFR rule: Disclose first. If the conflict cannot be managed in the client's best interest — AVOID it. You cannot simply disclose and proceed with a harmful conflict of interest." } },
  { q: "Which body allows a mutual fund dealer to operate in multiple provinces while dealing with only one 'Principal Regulator'?", opts: ["The Passport System (CSA)", "The OBSI", "FINTRAC", "The Bank of Canada"], correct: 0, fb: { y: "Correct! The Passport System, administered through the CSA, allows a registered firm to deal with one principal regulator and have that registration recognized across participating provinces — avoiding duplicate filings.", n: "Not quite. The Passport System is a CSA initiative. OBSI handles client complaints. FINTRAC handles money laundering reports. The Bank of Canada sets monetary policy." } },
  { q: "Under NI 81-105, which sales practice is PROHIBITED?", opts: ["Co-op marketing within strict dollar limits", "Providing small promotional items (pens, golf balls)", "Paying for an advisor's vacation disguised as a conference", "A dealer paying its own advisors a commission"], correct: 2, fb: { y: "Correct! NI 81-105 prohibits fund companies from paying for advisor vacations even if disguised as conferences. This creates an improper incentive that compromises objectivity. Co-op marketing within limits and small gifts are permitted.", n: "Not quite. Paying for vacations (disguised as conferences) is explicitly prohibited by NI 81-105. Small gifts, co-op marketing within limits, and normal dealer compensation are all permitted." } },
  { q: "How long does an advisor have to call a prospect after they make an inquiry, even if the prospect is on the DNCL?", opts: ["30 days", "3 months", "6 months", "18 months"], correct: 2, fb: { y: "Correct! An inquiry by a prospect gives you a 6-month window to call them — even if their number is on the National Do Not Call List. After 6 months, the exception expires.", n: "Not quite. Inquiry = 6 months to call. Existing client = 18 months after last transaction. Referral = one call. Do not confuse the 6-month inquiry period with the 18-month existing client period." } },
  { q: "Who has the ultimate power to create and enforce securities laws in Canada?", opts: ["The Federal Government", "The Provincial and Territorial Governments", "CIRO", "The TSX"], correct: 1, fb: { y: "Correct! Securities regulation is constitutionally a provincial responsibility in Canada. Each province and territory has its own securities commission. There is no federal securities regulator.", n: "Not quite. Provincial governments have ultimate authority over securities law in Canada — not the federal government. This is unique to Canada; most countries have a single national regulator." } },
  { q: "The Ombudsman for Banking Services and Investments (OBSI) provides:", opts: ["Insurance for deposits", "A database for advisor registration", "An independent dispute resolution service for clients", "Investment advice to retail investors"], correct: 2, fb: { y: "Correct! OBSI is an independent dispute resolution service — free for clients. If a client is unsatisfied with how their dealer handled a complaint, they escalate to OBSI. Its recommendations are non-binding but rarely ignored.", n: "Not quite. OBSI = dispute resolution, not deposit insurance (that's CDIC), not registration (that's NRD). OBSI is the last stop for unresolved client complaints before litigation." } },
  { q: "A 'Suspicious Transaction Report' (STR) must be filed:", opts: ["Only for cash transactions over $10,000", "Whenever an advisor suspects a transaction is related to money laundering, regardless of the amount", "Once per year as part of annual compliance", "When a client opens a TFSA account"], correct: 1, fb: { y: "Correct! An STR has NO dollar threshold. If there are reasonable grounds to suspect money laundering or terrorist financing — whether the amount is $100 or $100,000 — you must file an STR with FINTRAC immediately.", n: "Not quite. The STR has no minimum dollar amount. The $10,000 threshold applies only to Large Cash Transaction Reports. If suspicious activity exists at any amount, you must report it." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;
  function answer(idx) { if (answered[current] !== undefined) return; setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } }); }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }
  const q = questions[current], ans = answered[current];
  const pct = Math.round(score / total * 100);
  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 17 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>
      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) { if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; } else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. You've mastered the Canadian regulatory landscape." : score >= 8 ? "Excellent — review any missed questions." : score >= 6 ? "Good start. Revisit CIRO, the NIs, FINTRAC thresholds, and DNCL rules." : "Focus on the regulatory hierarchy, NI codes, FINTRAC reports, and CFR obligations."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "Provincial Jurisdiction", def: "Securities regulation in Canada is a provincial responsibility. Each of the 13 provinces and territories has its own securities commission or administrator." },
  { term: "CSA (Canadian Securities Administrators)", def: "Umbrella body of the 13 provincial regulators. Coordinates and harmonizes rules through National Instruments and the Passport System." },
  { term: "Passport System", def: "CSA mechanism allowing a firm registered in one province to deal with a single 'principal regulator' to operate across participating provinces." },
  { term: "National Instrument (NI)", def: "Rules adopted by all provincial regulators through the CSA. Provide consistent regulation across Canada." },
  { term: "CIRO", def: "Canadian Investment Regulatory Organization — formed Jan 1, 2023 by merging MFDA and IIROC. SRO that regulates investment and mutual fund dealers." },
  { term: "SRO (Self-Regulatory Organization)", def: "Industry body recognized by provincial regulators to set and enforce standards for member dealers. CIRO is the sole SRO for dealers in Canada." },
  { term: "OBSI", def: "Ombudsman for Banking Services and Investments — free, independent dispute resolution for banking and investment clients. Recommendations are non-binding but influential." },
  { term: "NI 81-101", def: "Disclosure rules — mandates the Fund Facts document and Simplified Prospectus for mutual funds." },
  { term: "NI 81-102", def: "Operations rules — governs what mutual funds can invest in, leverage limits, derivatives use, and short selling." },
  { term: "NI 81-105", def: "Sales Practices rules — regulates compensation and marketing. Prohibits excessive gifts, paid vacations, and tied selling." },
  { term: "NRD (National Registration Database)", def: "Web-based system for filing dealer and advisor registration applications electronically. All registrations filed through NRD." },
  { term: "Form 33-109F4", def: "The standard application form for an individual seeking registration as a Dealing Representative. Filed through NRD." },
  { term: "Dealing Representative", def: "Registration category for mutual fund salespeople. Requires: IFC exam, dealer sponsorship, good character." },
  { term: "Client Focused Reforms (CFRs)", def: "Regulatory changes fully implemented December 2021. Shifted industry standard to put client interest first. Covers RDI, conflict management, KYP, and KYC." },
  { term: "RDI (Relationship Disclosure Information)", def: "Document provided at account opening explaining the client-advisor relationship, products, costs, risks, and conflicts of interest." },
  { term: "Material Conflict of Interest", def: "A conflict that could reasonably affect the advisor's recommendations or the client's interest. Must be disclosed; if unmanageable, must be avoided." },
  { term: "KYP (Know Your Product)", def: "Obligation to understand a product's structure, risks, costs, and liquidity before recommending it. Cannot recommend what you don't understand." },
  { term: "FINTRAC", def: "Financial Transactions and Reports Analysis Centre of Canada — national financial intelligence unit combating money laundering and terrorist financing." },
  { term: "Large Cash Transaction Report", def: "FINTRAC report required for any cash transaction(s) totalling $10,000+ in a single 24-hour period." },
  { term: "Suspicious Transaction Report (STR)", def: "FINTRAC report required when there are reasonable grounds to suspect money laundering or terrorist financing — no dollar threshold." },
  { term: "PEP (Politically Exposed Person)", def: "Senior foreign official or close family member. High risk for corruption. Requires enhanced due diligence at account opening." },
  { term: "PIPEDA", def: "Personal Information Protection and Electronic Documents Act. Requires consent to collect personal data; data may only be used for the stated purpose." },
  { term: "DNCL (Do Not Call List)", def: "Operated by CRTC. Telemarketers cannot call registered numbers except: existing clients (18 months), inquiries (6 months), referrals (1 call)." },
];

// ── Main Export ──
export default function Chapter17() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "Regulatory Hierarchy — Provincial → CSA → CIRO → OBSI"],
    ["02", "National Instruments — NI 81-101, 81-102, 81-105"],
    ["03", "Registration — NRD, Form 33-109F4, Dealing Representative"],
    ["04", "Client Focused Reforms — RDI, Conflicts, KYP, KYC"],
    ["05", "FINTRAC — AML/ATF Reporting Obligations"],
    ["06", "Privacy (PIPEDA) & Do Not Call List (DNCL)"],
    ["🧮", "24-Hour FINTRAC Cash Checker"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 17
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              The Regulatory <span style={{ color: gold }}>Landscape</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              Provincial jurisdiction, the CSA and Passport System, CIRO, National Instruments, registration requirements, Client Focused Reforms, FINTRAC obligations, and privacy law — the complete compliance framework for Canadian mutual fund dealing.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","6","Core Topics"],["⏱","~20 min","read"],["🎯","10","practice questions"],["🧮","1","interactive tool"],["📋","3","NI deep dives"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* S1 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 01</span><span style={ss.badge}>Regulatory Structure</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>The Regulatory Hierarchy</h2>
            <p style={ss.prose}>
              Canada's securities regulation is <span style={{ color: gold, fontWeight: 600 }}>provincially driven</span> — there is no single federal regulator. Four layers work together from provincial authority down to day-to-day dealer oversight. Click each layer to expand:
            </p>
          </Reveal>
          <Reveal delay={0.1}><RegHierarchy /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="⚖️">
              <strong>CIRO Cannot Jail Anyone.</strong> CIRO is an SRO — it can fine, suspend, or expel member dealers and advisors, but it has no criminal powers. Only courts can impose imprisonment. This is a favourite exam distinction: regulatory discipline (CIRO) vs. criminal sanction (courts).
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S2 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 02</span><span style={ss.badge}>National Instruments</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Key National Instruments</h2>
            <p style={ss.prose}>
              Three National Instruments govern the mutual fund industry. Know which NI covers which topic — the exam tests these code numbers directly. Click each to expand:
            </p>
          </Reveal>
          <Reveal delay={0.1}><NITable /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="📌" accent={blue2}>
              <strong>NI Code Quick Reference:</strong> 81-<strong style={{ color: blue2 }}>101</strong> = Fund Facts (Disclosure). 81-<strong style={{ color: gold }}>102</strong> = Fund Operations (what funds can do). 81-<strong style={{ color: purple }}>105</strong> = Sales Practices (what advisors/fund companies can do together). Think: 101 = tell me about it, 102 = how it works, 105 = how it's sold.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S3 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 03</span><span style={ss.badge}>Registration</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Registration Requirements</h2>
            <p style={ss.prose}>
              To sell mutual funds in Canada, you must be registered as a <span style={{ color: gold, fontWeight: 600 }}>Dealing Representative</span> with your provincial securities commission — sponsored by a registered dealer, having passed the IFC exam:
            </p>
          </Reveal>
          <Reveal delay={0.1}><RegistrationPanel /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S4 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 04</span><span style={ss.badge}>Client Focused Reforms</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Compliance &amp; Client Focused Reforms (CFRs)</h2>
            <p style={ss.prose}>
              Fully implemented December 2021, the CFRs shifted the industry standard from "suitability" to <span style={{ color: gold, fontWeight: 600 }}>client interest first</span>. Four pillars — click each to explore what's required:
            </p>
          </Reveal>
          <Reveal delay={0.1}><CFRPanel /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="🔀" accent={red}>
              <strong style={{ color: red }}>Disclose → Manage → Avoid:</strong> The CFR conflict framework is sequential. First DISCLOSE the conflict in writing. Then attempt to MANAGE it in the client's best interest (e.g., justify a proprietary fund recommendation with solid suitability analysis). If the conflict cannot be managed without harming the client — AVOID the transaction entirely. There is no "proceed without disclosure" option.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S5 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 05</span><span style={ss.badge}>FINTRAC</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>FINTRAC — Anti-Money Laundering Obligations</h2>
            <p style={ss.prose}>
              Dealers and advisors are <span style={{ color: gold, fontWeight: 600 }}>Reporting Entities</span> under the PCMLTFA — they must report specific transactions and verify client identity. Three types of reports, with different thresholds:
            </p>
          </Reveal>
          <Reveal delay={0.1}><FINTRACPanel /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S6 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 06</span><span style={ss.badge}>Privacy & DNCL</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Privacy Law &amp; Do Not Call Rules</h2>
            <p style={ss.prose}>
              Two separate regimes govern how advisors can contact clients and use their data. PIPEDA covers data privacy; the DNCL governs unsolicited calls. Three exceptions allow calling numbers on the DNCL — know the time windows precisely:
            </p>
          </Reveal>
          <Reveal delay={0.1}><PrivacyPanel /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Quiz */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Knowledge Check</span><span style={ss.badge}>10 Questions</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Test Your Understanding</h2>
            <p style={ss.prose}>10 questions covering the regulatory hierarchy, CIRO, National Instruments, FINTRAC thresholds, CFR obligations, and DNCL rules from Chapter 17.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Glossary */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Quick Reference</span><span style={ss.badge}>Key Terms</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Glossary</h2>
            <p style={ss.prose}>All essential regulatory terms from Chapter 17.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
